import { cleaningTaskRepository, todoRepository } from '../db/database';
import { apiRequest, isApiConfigured } from './api';
import { hasNotificationPermission } from './notifications';

/**
 * Bildirim programının sunucuyla eşitlenmesi — CLAUDE.md §7.8.
 *
 * NEDEN GEREKLİ: iOS'ta bir PWA bildirimi kendi başına ZAMANLAYAMAZ
 * (§7.8.1). "Saat 14:00'te hatırlat" diyebilmenin tek yolu, o saati ve
 * neyin hatırlatılacağını sunucunun bilmesi ve push göndermesidir.
 *
 * GİZLİLİK: Bu dosya, kullanıcının yazdığı METİNLERİ (temizlik görev
 * adları ve yapılacak başlıkları) sunucuya gönderir. Bu bilinçli bir
 * karar (§7.8.4): kullanıcı, bildirimde madde adının görünmesini
 * istedi; bu da ancak sunucu o kelimeleri bilirse mümkün. Diğer her
 * şey (puanlar, alışkanlıklar, geçmiş) yalnızca cihazda kalır.
 */

/**
 * Sunucuya gönderilen veri.
 * Ham veri gönderiyoruz ("7 günde bir, en son 5 Eylül'de yapıldı"),
 * hesaplanmış bir "bugün sırası geldi" bayrağı değil. Sebep: cihaz
 * yalnızca uygulama açıkken çevrimiçi olur; hesaplanmış bir bayrak,
 * kullanıcı uygulamayı açmayı bıraktığı anda — yani hatırlatmanın en
 * çok gerektiği anda — bayatlardı.
 */
interface SyncPayload {
  timezone: string;
  cleaningTasks: {
    id: string;
    name: string;
    cycleDays: number;
    lastCompletedDate: string | null;
  }[];
  todos: { id: string; title: string; times: string[] }[];
}

/**
 * Programı sunucuya gönderir.
 *
 * Sessizce başarısız olur: bildirim eşitlemesi yan bir iştir, bir ağ
 * hatası yüzünden kullanıcının madde eklemesi engellenmemeli.
 *
 * İzin verilmemişse HİÇBİR ŞEY GÖNDERİLMEZ. Bildirim alamayacak bir
 * cihaz için kullanıcının yazdığı metinleri sunucuda tutmanın bir
 * karşılığı olmazdı.
 */
export async function syncNotificationSchedule(): Promise<void> {
  if (!isApiConfigured()) return;
  if (!hasNotificationPermission()) return;

  try {
    const [tasks, todos] = await Promise.all([
      cleaningTaskRepository.list(),
      // Yalnızca AÇIK maddeler: tamamlanmış bir işe bildirim
      // gönderilmez (§7.8.4). Liste sunucuda olduğu gibi yerine
      // geçtiği için, tamamlanan madde buradan düşünce hatırlatması
      // da kendiliğinden durur.
      todoRepository.listOpen(),
    ]);

    const payload: SyncPayload = {
      // Sunucu UTC çalışıyor; "14:00"ün hangi UTC anına denk geldiğini
      // bilmesi için saat dilimi şart (§7.8.1).
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cleaningTasks: tasks.map((task) => ({
        id: task.id,
        name: task.name,
        cycleDays: task.cycleDays,
        lastCompletedDate: task.lastCompletedDate,
      })),
      todos: todos
        .filter((todo) => todo.notificationsEnabled && todo.notificationTimes.length > 0)
        .map((todo) => ({
          id: todo.id,
          title: todo.title,
          times: todo.notificationTimes,
        })),
    };

    await apiRequest('/notification-sync', { method: 'POST', body: payload });
  } catch (error) {
    console.error('Bildirim programı eşitlenemedi:', error);
  }
}

/**
 * Eşitlemeyi "ateşle ve unut" biçiminde tetikler.
 *
 * Ekranlar bunu çağırır; sonucu beklemezler. Bir maddeyi işaretlemek,
 * sunucu cevabı gelene kadar ekranı bekletmemeli.
 */
export function queueNotificationSync(): void {
  void syncNotificationSchedule();
}
